import './Tracks.css'
import React, { Component } from 'react'
import * as R from 'ramda'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import SpinnerButton from './SpinnerButton'
import './Select.css'
import Track from './Track'
import Spinner from './Spinner'
import { Link } from 'react-router-dom'
import ToggleButton from './ToggleButton'
import GlobalSearchBar from './GlobalSearchBar'
import Popup from './Popup'
import CartSearchControls from './CartSearchControls'
import { isMobile } from 'react-device-detect'
import { trackMatchesAllTerms } from './searchTerms'
import { requestJSONwithCredentials } from './request-json-with-credentials'

const DESKTOP_REFRESH_MEDIA_QUERY = '(hover: hover) and (pointer: fine)'

const matchDesktopRefreshMediaQuery = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  try {
    return window.matchMedia(DESKTOP_REFRESH_MEDIA_QUERY)
  } catch (_e) {
    return null
  }
}

const evaluateDesktopRefreshGate = (mql) => {
  if (!mql || typeof mql.matches !== 'boolean') return true
  return mql.matches
}

const trackPassesEnabledStores = (track, enabledStores) =>
  enabledStores?.some((storeName) => track.stores?.find(R.propEq(storeName, 'name')))

const filterCartTracks = (tracks, terms, enabledStores) =>
  tracks.filter((track) => trackPassesEnabledStores(track, enabledStores) && trackMatchesAllTerms(track, terms))

class Tracks extends Component {
  constructor(props) {
    super(props)
    this.state = {
      selectedTrack: (props.tracks[0] || {}).id,
      currentTrack: -1,
      currentBelowScreen: false,
      currentAboveScreen: false,
      createdNotifications: new Set(),
      modifyingNotification: false,
      cartFilter: '',
      trackListFilterTerms: [],
      trackListFilterInputValue: '',
      trackListFilterEffectiveTerms: [],
      trackListFilterDebounce: undefined,
      trackListFilterGenres: [],
      visibleStartIndex: 0,
      visibleEndIndex: 50,
      pullDistance: 0,
      pullingToRefresh: false,
      showDesktopRefresh: evaluateDesktopRefreshGate(matchDesktopRefreshMediaQuery()),
    }
    this.handleScroll = this.handleScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.handleTouchStart = this.handleTouchStart.bind(this)
    this.handleTouchMove = this.handleTouchMove.bind(this)
    this.handleTouchEnd = this.handleTouchEnd.bind(this)
    this.handleDesktopRefreshMediaChange = this.handleDesktopRefreshMediaChange.bind(this)
    this.refreshTracks = this.refreshTracks.bind(this)
    this.desktopRefreshMediaQuery = null
    this.lastScrollTop = 0
    this.trackHeight = 34
    this.overscan = 10
    this.tbodyRef = React.createRef()
    this.trackHeights = new Map()
    this.resizeTimeout = null
    this.lastAutoLoadRequestedTrackCount = null
    this.pullStartY = null
    this.pullThreshold = 60
    this.pullMax = 120
  }

  /*
  componentDidMount() {
    if (this.props.search !== undefined) {
      this.triggerSearch().then(() => {})
    }
  }
   */

  getCartFilteredTracks() {
    return this.props.listState === 'carts'
      ? filterCartTracks(this.props.tracks, this.state.trackListFilterEffectiveTerms, this.props.enabledStores)
      : this.props.tracks
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize)

    this.desktopRefreshMediaQuery = matchDesktopRefreshMediaQuery()
    if (this.desktopRefreshMediaQuery) {
      const next = evaluateDesktopRefreshGate(this.desktopRefreshMediaQuery)
      if (next !== this.state.showDesktopRefresh) {
        this.setState({ showDesktopRefresh: next })
      }
      if (typeof this.desktopRefreshMediaQuery.addEventListener === 'function') {
        this.desktopRefreshMediaQuery.addEventListener('change', this.handleDesktopRefreshMediaChange)
      } else if (typeof this.desktopRefreshMediaQuery.addListener === 'function') {
        this.desktopRefreshMediaQuery.addListener(this.handleDesktopRefreshMediaChange)
      }
    }

    if (this.tbodyRef.current) {
      const scrollTop = this.tbodyRef.current.scrollTop
      const clientHeight = this.tbodyRef.current.clientHeight
      const tracks = this.getCartFilteredTracks()
      this.updateVisibleRange(scrollTop, clientHeight, tracks.length)
    }

    setTimeout(() => {
      const tracks = this.getCartFilteredTracks()
      this.measureTrackHeights(tracks)
      if (this.tbodyRef.current) {
        const scrollTop = this.tbodyRef.current.scrollTop
        const clientHeight = this.tbodyRef.current.clientHeight
        this.updateVisibleRange(scrollTop, clientHeight, tracks.length)
      }
      this.tryAutoLoadMoreWithoutScroll(tracks.length)
    }, 0)

    requestJSONwithCredentials({ path: '/genres' })
      .then((genres) => this.setState({ trackListFilterGenres: genres || [] }))
      .catch(() => {})
  }

  componentDidUpdate(prevProps, prevState) {
    const prevSelectedCartUuid = prevProps.selectedCart?.uuid
    const nextSelectedCartUuid = this.props.selectedCart?.uuid
    if (prevSelectedCartUuid !== nextSelectedCartUuid && this.props.listState === 'carts') {
      this.handleTrackListFilterClear()
    }

    if (
      prevProps.tracks !== this.props.tracks ||
      prevProps.listState !== this.props.listState ||
      prevState.trackListFilterEffectiveTerms !== this.state.trackListFilterEffectiveTerms ||
      prevProps.enabledStores !== this.props.enabledStores ||
      prevState.visibleStartIndex !== this.state.visibleStartIndex ||
      prevState.visibleEndIndex !== this.state.visibleEndIndex
    ) {
      setTimeout(() => {
        const tracks = this.getCartFilteredTracks()
        this.measureTrackHeights(tracks)
        if (this.tbodyRef.current) {
          const scrollTop = this.tbodyRef.current.scrollTop
          const clientHeight = this.tbodyRef.current.clientHeight
          this.updateVisibleRange(scrollTop, clientHeight, tracks.length)
        }
        this.tryAutoLoadMoreWithoutScroll(tracks.length)
      }, 0)
    }

    const tracks = this.getCartFilteredTracks()
    const tracksLengthChanged = prevProps.tracks.length !== this.props.tracks.length
    if (tracksLengthChanged || prevProps.loadingMore !== this.props.loadingMore || prevProps.hasMore !== this.props.hasMore) {
      this.tryAutoLoadMoreWithoutScroll(tracks.length)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }
    if (this.desktopRefreshMediaQuery) {
      if (typeof this.desktopRefreshMediaQuery.removeEventListener === 'function') {
        this.desktopRefreshMediaQuery.removeEventListener('change', this.handleDesktopRefreshMediaChange)
      } else if (typeof this.desktopRefreshMediaQuery.removeListener === 'function') {
        this.desktopRefreshMediaQuery.removeListener(this.handleDesktopRefreshMediaChange)
      }
      this.desktopRefreshMediaQuery = null
    }
  }

  handleDesktopRefreshMediaChange(event) {
    const next = typeof event?.matches === 'boolean' ? event.matches : evaluateDesktopRefreshGate(this.desktopRefreshMediaQuery)
    if (next !== this.state.showDesktopRefresh) {
      this.setState({ showDesktopRefresh: next })
    }
  }

  measureTrackHeights(tracks) {
    if (!this.tbodyRef.current) return
    
    let totalMeasured = 0
    let averageHeight = this.trackHeight
    
    const { visibleStartIndex, visibleEndIndex } = this.state
    const tbody = this.tbodyRef.current
    const trackElements = Array.from(tbody.querySelectorAll('tr.track'))
    
    trackElements.forEach((trackElement, localIndex) => {
      const actualIndex = visibleStartIndex + localIndex
      if (actualIndex < tracks.length) {
        const height = trackElement.getBoundingClientRect().height
        if (height > 0) {
          this.trackHeights.set(actualIndex, height)
          totalMeasured++
        }
      }
    })
    
    if (totalMeasured > 0) {
      const heights = Array.from(this.trackHeights.values())
      averageHeight = heights.reduce((sum, h) => sum + h, 0) / heights.length
      this.trackHeight = averageHeight
    }
  }

  getTrackHeight(index) {
    return this.trackHeights.get(index) || this.trackHeight
  }

  getTotalHeightUpToIndex(index, totalTracks) {
    let total = 0
    let measuredCount = 0
    let measuredTotal = 0
    
    for (let i = 0; i < index && i < totalTracks; i++) {
      const height = this.trackHeights.get(i)
      if (height) {
        total += height
        measuredCount++
        measuredTotal += height
      } else {
        total += this.trackHeight
      }
    }
    
    if (measuredCount > 0 && measuredCount < index) {
      const averageMeasured = measuredTotal / measuredCount
      if (Math.abs(averageMeasured - this.trackHeight) > 2) {
        this.trackHeight = averageMeasured
      }
    }
    
    return total
  }

  findVisibleRange(scrollTop, clientHeight, totalTracks) {
    let currentTop = 0
    let startIndex = 0
    let endIndex = totalTracks
    const overscanHeight = this.overscan * this.trackHeight
    
    for (let i = 0; i < totalTracks; i++) {
      const height = this.getTrackHeight(i)
      if (currentTop + height > scrollTop - overscanHeight) {
        startIndex = Math.max(0, i - this.overscan)
        break
      }
      currentTop += height
    }
    
    currentTop = 0
    for (let i = 0; i < totalTracks; i++) {
      currentTop += this.getTrackHeight(i)
      if (currentTop > scrollTop + clientHeight + overscanHeight) {
        endIndex = Math.min(totalTracks, i + this.overscan + 1)
        break
      }
    }
    
    return { startIndex, endIndex }
  }

  updateVisibleRange(scrollTop, clientHeight, totalTracks) {
    const { startIndex, endIndex } = this.findVisibleRange(scrollTop, clientHeight, totalTracks)
    
    if (
      startIndex !== this.state.visibleStartIndex ||
      endIndex !== this.state.visibleEndIndex
    ) {
      this.setState({
        visibleStartIndex: startIndex,
        visibleEndIndex: endIndex,
      })
    }
  }

  handleResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }

    this.resizeTimeout = setTimeout(() => {
      this.trackHeights.clear()
      const tracks = this.getCartFilteredTracks()
      this.measureTrackHeights(tracks)

      if (this.tbodyRef.current) {
        const scrollTop = this.tbodyRef.current.scrollTop
        const clientHeight = this.tbodyRef.current.clientHeight
        this.updateVisibleRange(scrollTop, clientHeight, tracks.length)
        this.tryAutoLoadMoreWithoutScroll(tracks.length)
      }
    }, 100)
  }

  tryAutoLoadMoreWithoutScroll(visibleTrackCount) {
    if (!this.tbodyRef.current || !this.props.onLoadMore) {
      return
    }

    if (!this.props.hasMore) {
      this.lastAutoLoadRequestedTrackCount = null
      return
    }

    if (this.props.loadingMore) {
      return
    }

    const { scrollHeight, clientHeight } = this.tbodyRef.current
    const hasScrollableOverflow = scrollHeight > clientHeight
    if (hasScrollableOverflow) {
      this.lastAutoLoadRequestedTrackCount = null
      return
    }

    if (this.lastAutoLoadRequestedTrackCount === visibleTrackCount) {
      return
    }

    this.lastAutoLoadRequestedTrackCount = visibleTrackCount
    this.props.onLoadMore()
  }

  handleScroll(event) {
    let currentBelowScreen = false
    let currentAboveScreen = false
    const current = document.querySelector('.playing')

    if (current) {
      const currentRect = current.getBoundingClientRect()
      const parentRect = current.parentElement.getBoundingClientRect()
      if (currentRect.y - parentRect.y > parentRect.height - 80) {
        currentBelowScreen = true
      } else if (currentRect.y - parentRect.y + currentRect.height < 0) {
        currentAboveScreen = true
      }
    }
    this.setState({ currentBelowScreen, currentAboveScreen })

    const target = this.tbodyRef.current || event.target
    const scrollTop = target.scrollTop
    const scrollHeight = target.scrollHeight
    const clientHeight = target.clientHeight
    const scrollBottom = scrollHeight - scrollTop - clientHeight
    const scrollingDown = scrollTop > this.lastScrollTop
    this.lastScrollTop = scrollTop

    const tracks = this.getCartFilteredTracks()

    this.updateVisibleRange(scrollTop, clientHeight, tracks.length)

    const preloadThreshold = clientHeight * 1.5

    if (        
      scrollingDown &&
      scrollBottom < preloadThreshold &&
      this.props.onLoadMore &&
      !this.props.loadingMore &&
      this.props.hasMore
    ) {
        this.props.onLoadMore()
    }
  }

  isPullToRefreshAvailable() {
    return ['new', 'recent', 'heard'].includes(this.props.listState) && !this.props.preview
  }

  handleTouchStart(event) {
    if (!this.isPullToRefreshAvailable()) return
    if (this.state.updatingTracks) return
    if (!this.tbodyRef.current || this.tbodyRef.current.scrollTop > 0) return
    this.pullStartY = event.touches[0].clientY
    this.setState({ pullingToRefresh: true })
  }

  handleTouchMove(event) {
    if (!this.state.pullingToRefresh) return
    if (!this.tbodyRef.current || this.tbodyRef.current.scrollTop > 0) {
      this.pullStartY = null
      this.setState({ pullingToRefresh: false, pullDistance: 0 })
      return
    }
    const delta = event.touches[0].clientY - this.pullStartY
    if (delta <= 0) {
      if (this.state.pullDistance !== 0) this.setState({ pullDistance: 0 })
      return
    }
    const resisted = Math.min(this.pullMax, delta * 0.5)
    if (Math.abs(resisted - this.state.pullDistance) > 0.5) {
      this.setState({ pullDistance: resisted })
    }
  }

  handleTouchEnd() {
    if (!this.state.pullingToRefresh) return
    const triggered = this.state.pullDistance >= this.pullThreshold
    this.pullStartY = null
    this.setState({ pullingToRefresh: false, pullDistance: 0 })
    if (triggered) {
      this.refreshTracks()
    }
  }

  scrollToTrack(trackId) {
    const tracks = this.props.tracks || []
    const index = tracks.findIndex(({ id }) => id === trackId)
    this.setState({ selectedTrack: trackId })
    if (index < 0 || !this.tbodyRef?.current) return
    this.tbodyRef.current.scrollTo({ top: this.getTotalHeightUpToIndex(index, tracks.length), behavior: 'smooth' })
  }

  scrollCurrentIntoView() {
    const current = document.querySelector('.playing')
    const currentRect = current.getBoundingClientRect()
    const parent = current.parentElement
    const parentRect = parent.getBoundingClientRect()
    parent.scrollBy({ top: currentRect.y - parentRect.y, behavior: 'smooth' })
  }

  onCartFilterChange(filter) {
    this.setState({ cartFilter: filter })
  }

  computeEffectiveTrackListTerms(committedTerms, inputValue) {
    const trimmed = (inputValue || '').trim()
    const partial = trimmed ? [{ type: 'text', value: trimmed }] : []
    return [...committedTerms, ...partial]
  }

  handleTrackListFilterChange(committedTerms, inputValue) {
    if (this.state.trackListFilterDebounce) {
      clearTimeout(this.state.trackListFilterDebounce)
    }

    const effectiveTerms = this.computeEffectiveTrackListTerms(committedTerms, inputValue)

    if (effectiveTerms.length === 0) {
      this.setState({
        trackListFilterTerms: committedTerms,
        trackListFilterInputValue: inputValue,
        trackListFilterEffectiveTerms: [],
        trackListFilterDebounce: undefined,
      })
      return
    }

    const timeout = setTimeout(() => {
      this.setState({
        trackListFilterEffectiveTerms: effectiveTerms,
        trackListFilterDebounce: undefined,
      })
    }, 300)

    this.setState({
      trackListFilterTerms: committedTerms,
      trackListFilterInputValue: inputValue,
      trackListFilterDebounce: timeout,
    })
  }

  handleTrackListFilterSearch(committedTerms, inputValue) {
    if (this.state.trackListFilterDebounce) {
      clearTimeout(this.state.trackListFilterDebounce)
    }
    this.setState({
      trackListFilterTerms: committedTerms,
      trackListFilterInputValue: inputValue,
      trackListFilterEffectiveTerms: this.computeEffectiveTrackListTerms(committedTerms, inputValue),
      trackListFilterDebounce: undefined,
    })
  }

  handleTrackListFilterClear() {
    if (this.state.trackListFilterDebounce) {
      clearTimeout(this.state.trackListFilterDebounce)
    }
    this.setState({
      trackListFilterTerms: [],
      trackListFilterInputValue: '',
      trackListFilterEffectiveTerms: [],
      trackListFilterDebounce: undefined,
    })
  }

  renderTracks(tracks) {
    const defaultCart = this.props.carts.find(R.prop('isDefault'))
    const { visibleStartIndex, visibleEndIndex } = this.state
    const visibleTracks = tracks.slice(visibleStartIndex, visibleEndIndex)
    const topSpacerHeight = this.getTotalHeightUpToIndex(visibleStartIndex, tracks.length)
    const bottomSpacerHeight = this.getTotalHeightUpToIndex(tracks.length, tracks.length) - 
                                this.getTotalHeightUpToIndex(visibleEndIndex, tracks.length)

    return (
      <>
        {topSpacerHeight > 0 && (
          <tr style={{ height: topSpacerHeight, display: 'block' }}>
            <td style={{ display: 'block', height: '100%' }} />
          </tr>
        )}
        {tracks.length !== 0 &&
          !this.props.searchInProgress &&
          visibleTracks.map((track, index) => {
            const actualIndex = visibleStartIndex + index
            const {
              id,
              title,
              mix,
              artists,
              remixers,
              labels,
              releases,
              released,
              published,
              previews,
              added,
              keys,
              genres,
              score,
              score_details,
              similarity,
              heard,
              stores,
              version,
              carts: trackCartsRaw,
            } = track
            const trackCarts = trackCartsRaw || []

            const storeSlugs = this.props.stores.map(({ storeName }) => storeName.toLowerCase())
            const storePreviews = previews.filter(({ store }) => storeSlugs.includes(store))
            const inCarts = this.props.carts.filter((cart) => trackCarts.some((c) => c && c.uuid === cart.uuid))
            const selectedCartId = this.props.selectedCart?.id
            return (
              <Track
                mode={this.props.mode}
                listState={this.props.listState}
                cartUuid={this.props.selectedCart?.uuid}
                carts={this.props.carts}
                cartFilter={this.state.cartFilter}
                defaultCartId={this.props.carts.find(R.prop('isDefault'))?.id}
                selectedCartId={selectedCartId}
                selectedCartIsPurchased={this.props.selectedCartIsPurchased}
                id={id}
                index={index}
                title={title}
                artists={artists}
                mix={mix}
                remixers={remixers}
                labels={labels}
                released={released}
                releases={releases}
                published={published}
                added={added}
                keys={keys}
                bpms={Array.from(new Set(stores.map(R.prop('bpm'))))}
                genres={genres}
                score={score}
                scoreDetails={score_details}
                similarity={similarity}
                cartSearch={this.props.cartSearch ? track.cartSearch : undefined}
                cartSearchGroups={this.props.cartSearch?.result?.groups}
                onCartSearchMiss={() => this.props.onCartSearchMiss(track)}
                trackStores={stores}
                stores={this.props.stores}
                selected={this.state.selectedTrack === id}
                playing={this.props.currentTrack === id}
                version={version}
                heard={heard}
                enabledStores={this.props.enabledStores}
                enabledStoreSearch={this.props.enabledStoreSearch}
                selectedCart={this.props.selectedCart}
                inDefaultCart={defaultCart ? trackCarts.some((c) => c && c.uuid === defaultCart.uuid) : false}
                inCurrentCart={inCarts.find(({ id }) => id === selectedCartId) !== undefined}
                inCarts={inCarts}
                popupAbove={tracks.length > 10 && tracks.length - actualIndex < 10}
                processingCart={this.props.processingCart}
                processingTrack={this.props.processingTrack}
                key={`track-${id}`}
                follows={this.props.follows}
                noPreviews={storePreviews.every(({ url, store }) => !url && store !== 'bandcamp')}
                onClick={() => {
                  this.setState({ selectedTrack: id })
                  this.props.onPreviewRequested(track)
                }}
                onDoubleClick={() => {
                  this.setState({ selectedTrack: id })
                  this.props.onPreviewRequested(track)
                }}
                onTouchTap={() => {
                  this.setState({ selectedTrack: id })
                  this.props.onPreviewRequested(track)
                }}
                onAddToCart={this.props.onAddToCart}
                onCreateCart={this.props.onCreateCart}
                onUpdateCarts={this.props.onUpdateCarts}
                onRemoveFromCart={this.props.onRemoveFromCart}
                onFollowClicked={() => {
                  this.props.onFollowClicked(track)
                }}
                onIgnoreClicked={() => {
                  this.props.onIgnoreClicked(track)
                }}
                onIgnoreArtistsByLabels={() =>
                  this.props.onIgnoreArtistsByLabels({
                    artistIds: artists.map(R.prop('id')),
                    labelIds: labels.map(R.prop('id')),
                  })
                }
                onCartButtonClick={this.props.onCartButtonClick}
                onCreateCartClick={this.props.onCreateCartClick}
                onMarkPurchasedButtonClick={this.props.onMarkPurchasedButtonClick}
                onCartFilterChange={this.onCartFilterChange.bind(this)}
                onMarkHeardButtonClick={this.props.onMarkHeardButtonClick}
                onToggleHeard={this.props.onToggleHeard}
                onAddEntityToSearch={this.props.onAddEntityToSearch}
              />
            )
          })}
        {bottomSpacerHeight > 0 && (
          <tr style={{ height: bottomSpacerHeight, display: 'block' }}>
            <td style={{ display: 'block', height: '100%' }} />
          </tr>
        )}
      </>
    )
  }

  async refreshTracks() {
    this.setState({ updatingTracks: true })
    try {
      await this.props.onUpdateTracksClicked()
    } finally {
      this.setState({ updatingTracks: false })
    }
  }

  isSubscribed(storeName) {
    const notificationSubscriptions = this.getNotificationSubscriptions()
    return notificationSubscriptions.find(({ storeName: name }) => storeName === name) !== undefined
  }

  render() {
    const scrollToCurrentButton = (
      <button
        className={'pill pill-button pill-button-glow'}
        onClick={this.scrollCurrentIntoView}
        style={{ padding: '4px 8px' }}
      >
        <span className="pill-button-contents">Scroll to current</span>
      </button>
    )

    const tracks = this.props.tracks

    const emptyListLabels = {
      search:
        this.props.searchError !== undefined
          ? this.props.searchError
          : this.props.searchInProgress
            ? 'Searching...'
            : 'No results',
      new: (
        <>
          No tracks available. Perhaps you need to{' '}
          <Link to={'/settings'}>
            <strong>follow more artists and labels</strong>
          </Link>
          ?
        </>
      ),
      heard: 'No tracks played',
      recent: 'No tracks added',
    }

    const emptyLabel = emptyListLabels[this.props.listState]
    const listInfo =
      this.props.listState === 'carts' && this.props.carts.length === 0 ? (
        <th>
          <Spinner />
          Loading carts...
        </th>
      ) : tracks.length === 0 && emptyLabel ? (
        <th>{emptyLabel}</th>
      ) : this.props.searchInProgress ? (
        <th>
          Searching <Spinner />
        </th>
      ) : this.props.fetchingCartDetails ? (
        <th>
          Fetching cart details <Spinner />
        </th>
      ) : this.props.listState === 'search' ? (
        <th>{tracks.length} results</th>
      ) : null

    return (
      <div
        style={{
          height: this.props.height,
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
        }}
        className="tracks"
      >
        {this.props.loading && (
          <div onMouseDown={(e) => e.stopPropagation()} className="loading-overlay">
            <Spinner size="large" />
          </div>
        )}
        <table className="tracks-table" style={{ display: 'flex', flexDirection: 'column' }}>
          <thead className={'noselect tracks-table-header'}>
            {this.props.listState === 'carts' && !listInfo && (
              <tr className={'top-bar input-layout'} style={{ width: '100%', background: 'rgb(34, 34, 34)' }}>
                <td
                  className="tracks-top_bar_group"
                  style={{
                    padding: 4,
                  }}
                >
                  {this.props.mode === 'app' ? (
                    <>
                      <GlobalSearchBar
                        placeholder={'Filter'}
                        terms={this.state.trackListFilterTerms}
                        loading={this.state.trackListFilterDebounce !== undefined}
                        className={'cart-filter'}
                        styles={'large'}
                        genres={this.state.trackListFilterGenres}
                        onChange={this.handleTrackListFilterChange.bind(this)}
                        onSearch={this.handleTrackListFilterSearch.bind(this)}
                        onClearSearch={this.handleTrackListFilterClear.bind(this)}
                      />
                      <span className={'cart-details'}>
                        Tracks in cart: {this.props.selectedCart?.trackCount}
                      </span>
                      {this.props.onFindSimilarToCart && this.props.selectedCart?.uuid && (
                        <button
                          type="button"
                          className="button button-push_button button-push_button-small button-push_button-primary"
                          data-testid="cart-find-similar"
                          title="Find tracks that sound like this cart, group by group"
                          onClick={() => this.props.onFindSimilarToCart(this.props.selectedCart)}
                        >
                          <FontAwesomeIcon icon="magnifying-glass" /> Find similar
                        </button>
                      )}
                    </>
                  ) : (
                    <span className="select_button-button select_button-button select_button-button__active">
                      {this.props.carts[0].name}
                    </span>
                  )}
                </td>
              </tr>
            )}

            {this.props.cartSearch ? (
              <>
                <CartSearchControls
                  cartSearch={this.props.cartSearch}
                  tracks={tracks}
                  searchInProgress={this.props.searchInProgress}
                  highlightedTrackId={this.state.selectedTrack}
                  onChange={this.props.onCartSearchChange}
                  onSaveGroup={this.props.onSaveCartSearchGroup}
                  onPointClick={(trackId) => this.scrollToTrack(trackId)}
                />
                {this.props.searchInProgress && (
                  <tr style={{ display: 'block', borderBottom: '1px solid black', padding: '0 8px', background: '#222' }}>
                    <th>
                      Searching <Spinner />
                    </th>
                  </tr>
                )}
                {!this.props.searchInProgress && tracks.length === 0 && (
                  <tr style={{ display: 'block', borderBottom: '1px solid black', padding: '0 8px', background: '#222' }}>
                    <th>{this.props.searchError || 'No results. Untick “New artists only”, clear the Not this list or remove other search terms.'}</th>
                  </tr>
                )}
              </>
            ) : (
              listInfo && (
                <tr style={{ display: 'block', borderBottom: '1px solid black', padding: '0 8px', background: '#222' }}>
                  {listInfo}
                </tr>
              )
            )}

            <tr className={'noselect tracks-table-header-columns'}>
              {this.props.mode === 'app' ? (
                <th className={'new-cell tracks-cell'}>
                  <div className={'new-cell-content track-table-cell'}>New</div>
                </th>
              ) : null}
              <th className={'track-details tracks-cell'}>
                <div className={'track-details-left track-details-content'}>
                  <div className={'artist-cell track-table-cell text-header-cell'}>Artist</div>
                  <div className={'title-cell track-table-cell text-header-cell'}>Title</div>
                  <div className={'label-cell track-table-cell text-header-cell'}>Label</div>
                </div>
                <div className={'track-details-center track-details-content'}>
                  {this.props.listState === 'recent' && (
                    <div className={'added-cell track-table-cell text-header-cell'}>Added</div>
                  )}
                  {this.props.listState !== 'recent' && (
                    <div className={'released-cell track-table-cell text-header-cell'}>Released</div>
                  )}
                </div>
                <div className={'track-details-right track-details-content'}>
                  <div className={'genre-cell track-table-cell'}>Genre</div>
                  <div className={'bpm-cell track-table-cell'}>BPM</div>
                  <div className={'key-cell track-table-cell'}>Key</div>
                </div>
              </th>
              {this.props.mode === 'app' && (
                <th className={'follow-ignore-cart-cell tracks-cell'}>
                  <div className={'score-cell track-table-cell'} style={this.props.cartSearch ? { textAlign: 'center' } : undefined}>
                    {this.props.listState === 'new'
                      ? 'Score'
                      : this.props.cartSearch
                        ? 'Fit'
                        : this.props.listState === 'search' && tracks[0]?.similarity !== undefined
                          ? 'Diff'
                          : ''}
                  </div>
                  <div className={'cart-cell track-table-cell'}>Cart</div>
                </th>
              )}
              <th className={'open-share-cell tracks-cell'}>
                <div className={'open-cell track-table-cell'}>
                  <Popup
                    popupStyle={{ flexDirection: 'column', minWidth: 150 }}
                    disabled={this.props.listState !== 'carts'}
                    style={{ display: 'block' }}
                    anchor={
                      <div
                        className={` ${
                          (this.props.listState === 'carts' &&
                            this.props.enabledStores &&
                            this.props.enabledStores.length < this.props.stores.length &&
                            'filter-active') ||
                          ''
                        }`}
                      >
                        {this.props.cartSearch ? (
                          <span title="Hide a result and push the search away from it (this session only)">Not this</span>
                        ) : (
                          <>Open {this.props.listState === 'carts' && <FontAwesomeIcon icon="caret-down" />}</>
                        )}
                      </div>
                    }
                  >
                    <>
                      <div>Enabled stores:</div>
                      {this.props.stores.map(({ storeName }) => {
                        const elementId = `${storeName}-enabled`
                        return (
                          <div
                            key={elementId}
                            className="input-layout"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}
                          >
                            <label htmlFor={elementId} className="noselect" style={{ flex: 1, textAlign: 'left' }}>
                              {storeName}
                            </label>
                            <div style={{ display: 'flex', justifyContent: 'space-around', flex: 0 }}>
                              <ToggleButton
                                id={elementId}
                                checked={this.props.enabledStores?.includes(storeName)}
                                onChange={() => this.props.onToggleStoreEnabled(storeName)}
                              />
                            </div>
                          </div>
                        )
                      })}
                      <hr className={'popup-divider'} />
                      <div>Show search for:</div>
                      {[...this.props.stores, { storeName: 'Youtube' }].map(({ storeName }) => {
                        const elementId = `${storeName}-search-enabled`
                        const storeDisabled =
                          this.props.stores.some(R.propEq(storeName, 'storeName')) &&
                          !this.props.enabledStores?.includes(storeName)
                        if (storeDisabled) return null
                        return (
                          <div
                            key={elementId}
                            className="input-layout"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}
                          >
                            <label htmlFor={elementId} className="noselect" style={{ flex: 1, textAlign: 'left' }}>
                              {storeName}
                            </label>
                            <div style={{ display: 'flex', justifyContent: 'space-around', flex: 0 }}>
                              <ToggleButton
                                id={elementId}
                                checked={storeDisabled ? false : this.props.enabledStoreSearch?.includes(storeName)}
                                onChange={() => this.props.onToggleStoreSearchEnabled(storeName)}
                              />
                            </div>
                          </div>
                        )
                      })}
                    </>
                  </Popup>
                </div>
              </th>
            </tr>
          </thead>
          <tbody
            ref={this.tbodyRef}
            style={{ overflow: 'scroll', display: 'block', flex: 1, overscrollBehaviorY: 'contain' }}
            onScroll={this.handleScroll}
            onTouchStart={this.handleTouchStart}
            onTouchMove={this.handleTouchMove}
            onTouchEnd={this.handleTouchEnd}
            onTouchCancel={this.handleTouchEnd}
          >
            {this.isPullToRefreshAvailable() && (this.state.pullDistance > 0 || this.state.updatingTracks) && (
              <tr
                style={{
                  display: 'block',
                  width: '100%',
                  height: this.state.updatingTracks ? 50 : this.state.pullDistance,
                  overflow: 'hidden',
                  transition: this.state.pullingToRefresh ? 'none' : 'height 200ms ease',
                }}
              >
                <td
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Spinner />
                  <span>
                    {this.state.updatingTracks
                      ? 'Refreshing tracks...'
                      : this.state.pullDistance >= this.pullThreshold
                        ? 'Release to refresh'
                        : 'Pull down to refresh'}
                  </span>
                </td>
              </tr>
            )}
            <tr style={{ width: '100%', background: 'none', position: 'fixed', zIndex: 1, marginTop: 3 }}>
              <td
                style={{
                  width: '100%',
                  display: this.state.currentAboveScreen ? 'flex' : 'none',
                  justifyContent: 'center',
                }}
              >
                {scrollToCurrentButton}
              </td>
            </tr>
            {!this.props.fetchingCartDetails &&
              this.renderTracks(
                this.props.listState === 'carts'
                  ? filterCartTracks(
                      tracks,
                      this.state.trackListFilterEffectiveTerms,
                      this.props.enabledStores,
                    )
                  : tracks,
              )}
            {this.props.loadingMore && tracks.length > 0 && (
              <tr style={{ display: 'flex', width: '100%', background: 'none' }}>
                <td
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    padding: 16,
                  }}
                >
                  <Spinner />
                  <span>Loading more tracks...</span>
                </td>
              </tr>
            )}
            <tr
              style={{
                margin: 0,
                display: 'flex',
                width: '100%',
                background: 'none',
                position: 'sticky',
                zIndex: 100,
                bottom: 0,
              }}
            >
              <td
                style={{
                  width: '100%',
                  display: this.state.currentBelowScreen ? 'flex' : 'none',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <div style={{ display: 'flex', position: 'absolute', bottom: '100%' }}>{scrollToCurrentButton}</div>
              </td>
            </tr>
          </tbody>
          {!this.props.preview &&
            this.state.showDesktopRefresh &&
            ['new', 'recent', 'heard'].includes(this.props.listState) && (
              <tfoot>
                <tr style={{ display: 'flex', justifyContent: 'center', background: 'rgb(34, 34, 34)' }}>
                  <td style={{ display: 'flex', gap: 16, margin: 4 }}>
                    <SpinnerButton
                      size={isMobile ? 'small' : 'large'}
                      loading={this.state.updatingTracks}
                      disabled={this.state.updatingTracks || this.props.loadingMore}
                      onClick={this.refreshTracks}
                      label={'Refresh'}
                      loadingLabel={'Refreshing'}
                    />
                  </td>
                </tr>
              </tfoot>
            )}
        </table>
      </div>
    )
  }
}

export default Tracks
